import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Plus, Trash2, Loader2, CheckCircle2, XCircle, Info, Building2 } from 'lucide-react';
import {
  useSesAccounts, useSaveSesAccount, useDeleteSesAccount, useTestSesAccount,
  useProperties, useSaveProperty,
  type SesAccount, type SesAccountInput,
} from '../../../hooks/useManagement';
import { useConfirm } from '../../../components/ConfirmDialog';
import { useToast } from '../../../components/ToastProvider';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import SaveButton from '../../../components/SaveButton';

const EMPTY: SesAccountInput = { environment: 'test', codigo_arrendador: '', aplicacion: 'Kanarix', usuario_ws: '', password_ws: '', enabled: true };

export default function ManageSesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: accounts, isLoading } = useSesAccounts();
  const { data: properties } = useProperties();
  const save = useSaveSesAccount();
  const del = useDeleteSesAccount();
  const test = useTestSesAccount();
  const saveProp = useSaveProperty();
  const confirm = useConfirm();

  const [form, setForm] = useState<SesAccountInput | null>(null);
  const [testResult, setTestResult] = useState<Record<number, { ok: boolean; msg: string }>>({});
  const { isDirty, resetBaseline } = useDirtyForm(form);
  const openForm = (draft: SesAccountInput) => { setForm(draft); resetBaseline(draft); };

  const editing = form?.id != null;
  const formValid = !!(form && form.codigo_arrendador?.trim() && form.usuario_ws?.trim() && (editing || form.password_ws?.trim()));

  const doSave = async () => {
    if (!form) return;
    try {
      await save.mutateAsync(form);
      toast.success(editing ? t('portal.manage.ses.updatedOk', 'Cuenta actualizada.') : t('portal.manage.ses.savedOk', 'Cuenta SES creada.'));
      setForm(null);
    } catch { toast.error(t('portal.manage.ses.saveError', 'No se ha podido guardar. Revisa los datos.')); }
  };

  const runTest = async (id: number) => {
    try {
      const r = await test.mutateAsync(id);
      const msg = r.ok
        ? t('portal.manage.ses.testOk', 'Conexión correcta ✓')
        : t('portal.manage.ses.testFail', 'Error {{code}}: {{desc}}', { code: r.codigoRetorno || r.httpStatus, desc: r.descripcion || t('portal.manage.ses.testBadCreds', 'credenciales no válidas') });
      setTestResult(s => ({ ...s, [id]: { ok: r.ok, msg } }));
    } catch { setTestResult(s => ({ ...s, [id]: { ok: false, msg: t('portal.manage.ses.testNetwork', 'No se ha podido contactar con SES.') } })); }
  };

  const removeAccount = async (a: SesAccount) => {
    if ((await confirm({
      title: t('portal.manage.ses.deleteTitle', 'Eliminar cuenta SES'),
      message: t('portal.manage.ses.deleteConfirm', '¿Eliminar la cuenta "{{name}}"? Las propiedades que la usen quedarán sin configurar.', { name: a.label || a.codigo_arrendador }),
      confirmLabel: t('portal.manage.ses.deleteYes', 'Eliminar'),
      tone: 'danger',
    })) !== 'confirm') return;
    await del.mutateAsync(a.id);
    toast.success(t('portal.manage.ses.deletedOk', 'Cuenta eliminada.'));
  };

  const mapProperty = async (propertyId: number, patch: { ses_account_id?: number | null; ses_codigo_establecimiento?: string }) => {
    try {
      await saveProp.mutateAsync({ id: propertyId, ...patch });
    } catch { toast.error(t('portal.manage.ses.mapError', 'No se ha podido guardar la asignación.')); }
  };

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck size={22} className="text-teal-600" /> {t('portal.manage.ses.title', 'SES.Hospedajes — Registro de viajeros')}</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">{t('portal.manage.ses.subtitle', 'Envío del parte de viajeros al Ministerio del Interior (Real Decreto 933/2021). Configura tu cuenta SES y asigna a cada alojamiento su código de establecimiento. Después, envía cada reserva manualmente desde Reservas.')}</p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2 max-w-3xl">
        <Info size={16} className="mt-0.5 shrink-0" />
        <div>{t('portal.manage.ses.help', 'Necesitas activar «Envío de comunicaciones por servicio web» en tu cuenta de SES.Hospedajes (Mis datos registrados). Recibirás por email tu Usuario WS y Contraseña WS. El Código de arrendador y los Códigos de establecimiento aparecen en tu registro. Usa primero el entorno de pruebas.')}</div>
      </div>

      {/* ── Accounts ── */}
      <section className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900">{t('portal.manage.ses.accountsTitle', 'Cuentas SES')}</h2>
          {!form && <button onClick={() => openForm({ ...EMPTY })} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg"><Plus size={15} /> {t('portal.manage.ses.addAccount', 'Añadir cuenta')}</button>}
        </div>

        {form && (
          <form onSubmit={e => { e.preventDefault(); void doSave(); }} className="p-5 border-b border-slate-100 bg-slate-50/60 grid sm:grid-cols-2 gap-4">
            <Field label={t('portal.manage.ses.fieldLabel', 'Etiqueta (opcional)')}><input value={form.label ?? ''} onChange={e => setForm({ ...form, label: e.target.value })} placeholder={t('portal.manage.ses.labelPlaceholder', 'Ej. Propietario / Sociedad')} className={inputCls} /></Field>
            <Field label={t('portal.manage.ses.fieldEnv', 'Entorno')}>
              <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value as 'test' | 'prod' })} className={inputCls}>
                <option value="test">{t('portal.manage.ses.envTest', 'Pruebas (pre-ses)')}</option>
                <option value="prod">{t('portal.manage.ses.envProd', 'Producción')}</option>
              </select>
            </Field>
            <Field label={t('portal.manage.ses.fieldArrendador', 'Código de arrendador *')}><input required value={form.codigo_arrendador ?? ''} onChange={e => setForm({ ...form, codigo_arrendador: e.target.value })} className={inputCls} /></Field>
            <Field label={t('portal.manage.ses.fieldApp', 'Aplicación')}><input value={form.aplicacion ?? ''} onChange={e => setForm({ ...form, aplicacion: e.target.value })} className={inputCls} /></Field>
            <Field label={t('portal.manage.ses.fieldUser', 'Usuario WS *')}><input required value={form.usuario_ws ?? ''} onChange={e => setForm({ ...form, usuario_ws: e.target.value })} placeholder="NIF + WS" className={inputCls} /></Field>
            <Field label={editing ? t('portal.manage.ses.fieldPasswordEdit', 'Contraseña WS (dejar vacío = sin cambios)') : t('portal.manage.ses.fieldPassword', 'Contraseña WS *')}><input type="password" required={!editing} value={form.password_ws ?? ''} onChange={e => setForm({ ...form, password_ws: e.target.value })} placeholder="••••••••" className={inputCls} /></Field>
            <div className="sm:col-span-2 flex items-center gap-2">
              <SaveButton onClick={doSave} saving={save.isPending} isDirty={isDirty && formValid} label={t('portal.manage.ses.save', 'Guardar')} savedLabel={t('common.saved', 'Guardado')} size="sm" />
              <button type="button" onClick={() => setForm(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">{t('portal.manage.ses.cancel', 'Cancelar')}</button>
            </div>
          </form>
        )}

        <div className="divide-y divide-slate-100">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-teal-500" /></div>
          ) : !accounts?.length ? (
            <div className="px-5 py-8 text-sm text-slate-400 text-center">{t('portal.manage.ses.noAccounts', 'No hay cuentas SES configuradas todavía.')}</div>
          ) : accounts.map(a => {
            const tr = testResult[a.id];
            return (
              <div key={a.id} className="p-5 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    {a.label || a.codigo_arrendador}
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${a.environment === 'prod' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>{a.environment === 'prod' ? t('portal.manage.ses.badgeProd', 'Producción') : t('portal.manage.ses.badgeTest', 'Pruebas')}</span>
                    {!a.enabled && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">{t('portal.manage.ses.badgeDisabled', 'Desactivada')}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{t('portal.manage.ses.metaArrendador', 'Arrendador')} {a.codigo_arrendador} · {t('portal.manage.ses.metaUser', 'Usuario')} {a.usuario_ws} · {a.has_password ? t('portal.manage.ses.passwordSaved', 'contraseña guardada 🔒') : t('portal.manage.ses.noPassword', 'sin contraseña')}</div>
                  {tr && <div className={`text-xs mt-1 inline-flex items-center gap-1 ${tr.ok ? 'text-emerald-600' : 'text-rose-600'}`}>{tr.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {tr.msg}</div>}
                </div>
                <button onClick={() => runTest(a.id)} disabled={test.isPending} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5">{test.isPending ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {t('portal.manage.ses.testConnection', 'Probar conexión')}</button>
                <button onClick={() => openForm({ id: a.id, label: a.label ?? '', environment: a.environment, codigo_arrendador: a.codigo_arrendador, aplicacion: a.aplicacion, usuario_ws: a.usuario_ws, password_ws: '', enabled: !!a.enabled })} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">{t('portal.manage.ses.edit', 'Editar')}</button>
                <button onClick={() => removeAccount(a)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg"><Trash2 size={15} /></button>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Per-property mapping ── */}
      <section className="rounded-2xl border border-slate-200 bg-white">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Building2 size={18} className="text-teal-600" /> {t('portal.manage.ses.mapTitle', 'Alojamientos → establecimiento SES')}</h2>
          <p className="text-xs text-slate-500 mt-1">{t('portal.manage.ses.mapSubtitle', 'Asigna a cada alojamiento su cuenta SES y su código de establecimiento (10 dígitos).')}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wider">
              <th className="px-5 py-3">{t('portal.manage.ses.mapProperty', 'Alojamiento')}</th><th className="px-5 py-3">{t('portal.manage.ses.mapAccount', 'Cuenta SES')}</th><th className="px-5 py-3">{t('portal.manage.ses.mapCode', 'Código establecimiento')}</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(properties ?? []).map(p => (
                <tr key={p.id}>
                  <td className="px-5 py-3 font-medium text-slate-800 max-w-[20rem] truncate">{p.name}</td>
                  <td className="px-5 py-3">
                    <select value={p.ses_account_id ?? ''} onChange={e => mapProperty(p.id, { ses_account_id: e.target.value ? Number(e.target.value) : null })} className={`${inputCls} min-w-[12rem]`}>
                      <option value="">{t('portal.manage.ses.mapNone', '— Sin asignar —')}</option>
                      {(accounts ?? []).map(a => <option key={a.id} value={a.id}>{a.label || a.codigo_arrendador}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-3">
                    <input defaultValue={p.ses_codigo_establecimiento ?? ''} onBlur={e => { if (e.target.value !== (p.ses_codigo_establecimiento ?? '')) mapProperty(p.id, { ses_codigo_establecimiento: e.target.value }); }} placeholder="1234567890" className={`${inputCls} w-40`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white';
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>{children}</label>;
}
